import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { v4 as uuidv4 } from 'uuid';
import type { QuestionContent, MultipleChoiceContent, MultipleChoiceOption } from '@/types';
import styles from './QuestionEditor.module.scss';

interface MultipleChoiceEditorProps {
  content: QuestionContent | null;
  onChange: (content: QuestionContent) => void;
}

export function MultipleChoiceEditor({ content, onChange }: MultipleChoiceEditorProps) {
  const [options, setOptions] = useState<MultipleChoiceOption[]>([]);
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);

  // Initialize from existing content
  useEffect(() => {
    if (content && content.type === 'multiple_choice') {
      setOptions(content.options);
      setAllowMultipleAnswers(content.allowMultipleAnswers || false);
    } else {
      // Start with 4 empty options
      setOptions([
        { id: uuidv4(), text: '', isCorrect: false },
        { id: uuidv4(), text: '', isCorrect: false },
        { id: uuidv4(), text: '', isCorrect: false },
        { id: uuidv4(), text: '', isCorrect: false },
      ]);
    }
  }, [content]);

  // Update parent whenever options or settings change
  useEffect(() => {
    if (options.length >= 2 && options.every((opt) => opt.text.trim() !== '')) {
      const newContent: MultipleChoiceContent = {
        type: 'multiple_choice',
        options,
        allowMultipleAnswers,
      };
      onChange(newContent);
    }
  }, [options, allowMultipleAnswers, onChange]);

  const handleOptionTextChange = (id: string, text: string) => {
    setOptions((prev) =>
      prev.map((opt) => (opt.id === id ? { ...opt, text } : opt))
    );
  };

  const handleToggleCorrect = (id: string) => {
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.id === id) {
          return { ...opt, isCorrect: !opt.isCorrect };
        }
        // If not allowing multiple answers, uncheck other options
        if (!allowMultipleAnswers && opt.isCorrect) {
          return { ...opt, isCorrect: false };
        }
        return opt;
      })
    );
  };

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions((prev) => [...prev, { id: uuidv4(), text: '', isCorrect: false }]);
    }
  };

  const handleRemoveOption = (id: string) => {
    if (options.length > 2) {
      setOptions((prev) => prev.filter((opt) => opt.id !== id));
    }
  };

  const hasAtLeastOneCorrect = options.some((opt) => opt.isCorrect);

  return (
    <div className={styles.editor}>
      <h4 className={styles.editor__heading}>Answer Options</h4>

      <div className={styles.editor__field}>
        <label className={styles.editor__checkboxLabel}>
          <input
            type="checkbox"
            className={styles.editor__checkbox}
            checked={allowMultipleAnswers}
            onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
          />
          <span className={styles.editor__checkboxText}>
            Allow multiple correct answers
          </span>
        </label>
      </div>

      <div className={styles.editor__optionsList}>
        <AnimatePresence>
          {options.map((option, index) => (
            <motion.div
              key={option.id}
              className={styles.editor__option}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05 }}
              layout
            >
              <div className={styles.editor__optionHeader}>
                <span className={styles.editor__optionNumber}>{index + 1}</span>
                <label className={styles.editor__optionCorrect}>
                  <input
                    type="checkbox"
                    className={styles.editor__checkbox}
                    checked={option.isCorrect}
                    onChange={() => handleToggleCorrect(option.id)}
                  />
                  <span className={styles.editor__checkboxText}>Correct</span>
                </label>
              </div>

              <div className={styles.editor__optionContent}>
                <input
                  type="text"
                  className={`${styles.editor__input} ${
                    option.isCorrect ? styles['editor__input--correct'] : ''
                  }`}
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  maxLength={200}
                />
                {options.length > 2 && (
                  <motion.button
                    className={styles.editor__removeButton}
                    onClick={() => handleRemoveOption(option.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ×
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {options.length < 10 && (
        <motion.button
          className={styles.editor__addButton}
          onClick={handleAddOption}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          + Add Option
        </motion.button>
      )}

      {!hasAtLeastOneCorrect && options.some((opt) => opt.text.trim() !== '') && (
        <p className={styles.editor__warning}>
          ⚠️ At least one option must be marked as correct
        </p>
      )}
    </div>
  );
}
